import { Step, StepId, StepType } from "@redotech/redo-return-ui/flows/step";
import { createStepFromType, generateStepId } from "./create-step";
import { FlowEditorDragType } from "./step-type-mapping";

export interface DropResult {
  steps: Map<StepId, Step>;
  newStepId: StepId;
  position: { x: number; y: number };
}

export function snapToGrid(
  position: { x: number; y: number },
  gridSize: number,
): { x: number; y: number } {
  return {
    x: Math.round(position.x / gridSize) * gridSize,
    y: Math.round(position.y / gridSize) * gridSize,
  };
}

export function handleStepDropped({
  dragType,
  position,
  steps,
  stepOptions,
  gridSize = 20,
}: {
  dragType: FlowEditorDragType;
  position: { x: number; y: number };
  steps: Map<StepId, Step>;
  stepOptions: StepType<any, any>[];
  gridSize?: number;
}): DropResult | null {
  const stepType = stepOptions.find((st) => {
    const title = st.title.toLowerCase();
    switch (dragType) {
      case FlowEditorDragType.AI_RETURN_REASONS:
        return title === "ai return reasons";
      case FlowEditorDragType.CONDITION:
        return title === "condition";
      case FlowEditorDragType.MULTIPLE_CHOICE:
        return title === "multiple choice";
      case FlowEditorDragType.RETURN_REASON:
        return (
          title === "return reason" ||
          title === "claim reason" ||
          title === "warranty reason"
        );
      case FlowEditorDragType.INPUT:
        return title === "input";
      case FlowEditorDragType.INFORMATION:
        return title === "information";
      case FlowEditorDragType.AB_TEST:
        return title === "a/b test";
      case FlowEditorDragType.REJECT:
        return title === "reject";
      case FlowEditorDragType.ADD_RETURN_TAG:
        return (
          title === "add return tag" ||
          title === "add claim tag" ||
          title === "add warranty tag"
        );
      case FlowEditorDragType.BLOCK:
        return title === "block";
      case FlowEditorDragType.RETURN:
        return title === "return";
      case FlowEditorDragType.FLAG:
        return title === "flag";
      case FlowEditorDragType.MANUAL_REVIEW:
        return title === "mark for manual review";
      case FlowEditorDragType.OVERRIDE_ADJUSTMENT:
        return title === "override adjustment";
      case FlowEditorDragType.OVERRIDE_GREEN_RETURN:
        return title === "require send back";
      case FlowEditorDragType.DYNAMIC_BUNDLE_ADJUSTMENT:
        return title === "dynamic bundle adjustment";
      case FlowEditorDragType.EXCHANGE_SHIPPING_NAME:
        return title === "shipping name";
      case FlowEditorDragType.DISCOUNT_CODE_NAME:
        return title === "discount code name";
      case FlowEditorDragType.SUBMIT_CLAIM:
        return title === "submit claim";
      case FlowEditorDragType.SUBMIT_REPAIR:
        return title === "submit repair";
      case FlowEditorDragType.SUBMIT_WARRANTY_REGISTRATION:
        return title === "submit warranty registration";
      case FlowEditorDragType.SUBMIT_WARRANTY:
        return title === "submit warranty";
      case FlowEditorDragType.SET_RETURN_LOCATIONS:
        return title === "location";
      case FlowEditorDragType.REDO_MANAGED_LOCATION:
        return title === "returns center" || title === "redo returns center";
      case FlowEditorDragType.COMBINE_LABEL:
        return title === "combine label";
      case FlowEditorDragType.SEPARATE_LABELS:
        return title === "separate labels";
      case FlowEditorDragType.RETURN_PROCESSING_DECISION:
        return title === "decision";
      case FlowEditorDragType.EVIDENCE:
        return title === "capture evidence";
      case FlowEditorDragType.AGENT_VERIFICATION:
        return title === "agent verification";
      default:
        return false;
    }
  });

  if (!stepType) {
    console.error(`No step type found for drag type: ${dragType}`);
    return null;
  }

  const newStepId = generateStepId(steps);

  const newStep = createStepFromType(stepType, steps);

  const snappedPosition = snapToGrid(position, gridSize);

  const updatedSteps = new Map(steps);
  updatedSteps.set(newStepId, newStep);

  return { steps: updatedSteps, newStepId, position: snappedPosition };
}
